import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Bell,
  Bookmark,
  Cake,
  CreditCard,
  LogOut,
  Mic,
  Palette,
  Search,
  Settings,
  Shield,
  Star,
  User,
  Users,
  X,
} from 'lucide-react';
import { Avatar, LanguageSwitcher, Pill } from '@/components/ui';

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  signedIn: boolean;
  isPremium: boolean;
  isAdmin: boolean;
  canManage: boolean;
  canOwn: boolean;
  artistSlug: string;
  displayName: string;
  handle: string | null;
  avatarSeed: string;
  avatarUrl: string | null;
  unreadCount: number;
  onSignOut: () => void;
}

/**
 * Slide-in-from-right drawer that holds every nav action on small screens.
 * Desktop TopBar keeps its icon cluster; this opens when the user taps the
 * hamburger below md. Focus is trapped, body scroll locks, Esc + backdrop
 * close it.
 */
export function MobileNavDrawer({
  open,
  onClose,
  signedIn,
  isPremium,
  isAdmin,
  canManage,
  canOwn,
  artistSlug,
  displayName,
  handle,
  avatarSeed,
  avatarUrl,
  unreadCount,
  onSignOut,
}: MobileNavDrawerProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
      className="fixed inset-0 z-40 md:hidden"
    >
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <aside className="absolute bottom-0 right-0 top-0 flex w-[86%] max-w-[360px] flex-col bg-surface shadow-2xl">
        <header className="flex items-center justify-between border-b border-[color:var(--color-border)] px-4 py-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-on-surface-muted">
            Menu
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-9 items-center justify-center rounded-full text-on-surface-muted transition hover:bg-surface-2 hover:text-on-surface"
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>

        <div className="flex flex-1 flex-col overflow-y-auto">
          {signedIn && (
            <Link
              to={handle ? `/@${handle}` : '/settings/profile'}
              onClick={onClose}
              className="flex items-center gap-3 border-b border-[color:var(--color-border)] px-4 py-4 transition hover:bg-surface-2"
            >
              <Avatar
                seed={avatarSeed}
                name={displayName}
                src={avatarUrl}
                size={40}
              />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-semibold text-on-surface">
                  {displayName}
                </span>
                {handle && (
                  <span className="truncate text-xs text-on-surface-muted">
                    @{handle}
                  </span>
                )}
                {isPremium && (
                  <Pill tone="primary" className="mt-1 self-start">
                    <Star className="size-3 fill-current" aria-hidden />
                    Superfan
                  </Pill>
                )}
              </div>
            </Link>
          )}

          <nav className="flex flex-1 flex-col px-2 py-2">
            <DrawerItem
              to="/search"
              icon={<Search className="size-[18px]" aria-hidden />}
              label={t('topBar.search')}
              onClose={onClose}
            />

            {signedIn && (
              <>
                {handle && (
                  <DrawerItem
                    to={`/@${handle}`}
                    icon={<User className="size-[18px]" aria-hidden />}
                    label="My profile"
                    onClose={onClose}
                  />
                )}
                <DrawerItem
                  to="/settings/profile"
                  icon={<Settings className="size-[18px]" aria-hidden />}
                  label="Edit profile"
                  onClose={onClose}
                />
                <DrawerItem
                  to="/settings/notifications"
                  icon={<Bell className="size-[18px]" aria-hidden />}
                  label={t('topBar.notifications')}
                  {...(unreadCount > 0
                    ? { badge: String(unreadCount) }
                    : {})}
                  onClose={onClose}
                />
                <DrawerItem
                  to="/bookmarks"
                  icon={<Bookmark className="size-[18px]" aria-hidden />}
                  label="Bookmarks"
                  onClose={onClose}
                />
                {/* Orders and Rewards are hidden from the drawer for now
                    but their routes still work if linked directly. */}
                <DrawerItem
                  to="/membership"
                  icon={<CreditCard className="size-[18px]" aria-hidden />}
                  label="Membership"
                  onClose={onClose}
                />
              </>
            )}

            {signedIn && isAdmin && (
              <DrawerItem
                to="/admin/moderation"
                icon={<Shield className="size-[18px]" aria-hidden />}
                label={t('topBar.admin')}
                onClose={onClose}
              />
            )}
            {signedIn && isAdmin && (
              <DrawerItem
                to="/admin/birthdays"
                icon={<Cake className="size-[18px]" aria-hidden />}
                label="Birthdays"
                onClose={onClose}
              />
            )}

            {signedIn && canManage && (
              <>
                <DrawerDivider label="Artist studio" />
                <DrawerItem
                  to={`/artists/${artistSlug}/studio`}
                  icon={<Mic className="size-[18px]" aria-hidden />}
                  label="Studio (post as artist)"
                  accent
                  onClose={onClose}
                />
                <DrawerItem
                  to={`/artists/${artistSlug}/studio/settings`}
                  icon={<Palette className="size-[18px]" aria-hidden />}
                  label="Branding"
                  onClose={onClose}
                />
                <DrawerItem
                  to={`/artists/${artistSlug}/studio/analytics`}
                  icon={<BarChart3 className="size-[18px]" aria-hidden />}
                  label="Analytics"
                  onClose={onClose}
                />
                {canOwn && (
                  <DrawerItem
                    to={`/artists/${artistSlug}/studio/team`}
                    icon={<Users className="size-[18px]" aria-hidden />}
                    label="Team"
                    onClose={onClose}
                  />
                )}
              </>
            )}

            <DrawerDivider label="Preferences" />
            <div className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm text-on-surface">
              <span>{t('common.language')}</span>
              <LanguageSwitcher />
            </div>

            {!signedIn && (
              <div className="mt-2 flex flex-col gap-2 px-3 py-2">
                <Link
                  to="/login"
                  onClick={onClose}
                  className="inline-flex h-11 items-center justify-center rounded-full border border-[color:var(--color-border-strong)] px-4 text-sm font-medium text-on-surface hover:border-primary hover:text-primary"
                >
                  {t('topBar.signIn')}
                </Link>
                <Link
                  to="/signup"
                  onClick={onClose}
                  className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-4 font-display text-sm font-semibold text-[color:var(--color-primary-ink)] hover:bg-secondary"
                >
                  {t('topBar.joinFree')}
                </Link>
              </div>
            )}

            {signedIn && !isPremium && (
              <Link
                to="/membership"
                onClick={onClose}
                className="mx-3 mt-3 inline-flex h-11 items-center justify-center gap-2 rounded-full bg-accent px-4 font-display text-sm font-semibold text-[color:var(--color-accent-ink)] hover:opacity-90"
              >
                <Star className="size-4 fill-current" aria-hidden />
                {t('topBar.goSuperfan')}
              </Link>
            )}
          </nav>

          {signedIn && (
            <div className="border-t border-[color:var(--color-border)] px-3 py-3">
              <button
                type="button"
                onClick={() => {
                  onSignOut();
                  onClose();
                }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm text-on-surface-dim transition hover:bg-surface-2 hover:text-on-surface"
              >
                <LogOut className="size-[18px]" aria-hidden />
                {t('topBar.signOut')}
              </button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function DrawerItem({
  to,
  icon,
  label,
  accent,
  badge,
  onClose,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  accent?: boolean;
  badge?: string;
  onClose: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className={[
        'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition',
        accent
          ? 'bg-[color:var(--color-primary-dim)] text-primary hover:bg-primary hover:text-[color:var(--color-primary-ink)]'
          : 'text-on-surface hover:bg-surface-2',
      ].join(' ')}
    >
      <span
        className={[
          'flex size-8 items-center justify-center rounded-lg',
          accent
            ? 'bg-primary text-[color:var(--color-primary-ink)]'
            : 'bg-surface-3 text-on-surface-dim',
        ].join(' ')}
      >
        {icon}
      </span>
      <span className="flex-1 font-medium">{label}</span>
      {badge && (
        <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-[color:var(--color-primary-ink)]">
          {badge}
        </span>
      )}
    </Link>
  );
}

function DrawerDivider({ label }: { label: string }) {
  return (
    <div className="mt-3 px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-muted">
      {label}
    </div>
  );
}

export default MobileNavDrawer;
